import React from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DeleteSharp } from "@mui/icons-material";
import { DateTimePicker, LocalizationProvider, DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";
import { notification } from "src/utils/messages";

function ScheduleEntryRow({
  entry,
  index,
  totalEntries,
  hasImmediateEntry,
  onUpdate,
  onRemove,
  isEditMode
}) {
  const isMain = index === 0;
  const isSent = String(entry?.notification_send_status) === "1";
  const effectiveReadOnly = isSent;

  const handleStartDateChange = (newDate) => {
    if (newDate) {
      const formatted = moment(newDate).format("DD-MM-YYYY");
      let updated = { ...entry, s_date: formatted };
      if (
        entry.e_date &&
        moment(entry.e_date, "DD-MM-YYYY").isBefore(moment(formatted, "DD-MM-YYYY"))
      ) {
        updated.e_date = "";
        notification({ type: "error", message: "End date cannot be less than start date." })();
      }
      onUpdate(index, updated);
    }
  };

  const handleEndDateChange = (newDate) => {
    if (newDate)
      onUpdate(index, { ...entry, e_date: moment(newDate).format("DD-MM-YYYY") });
  };

  const handleSendTypeChange = (e) => {
    const value = e.target.value;
    if (value === "1" && hasImmediateEntry && entry.notification_send_type !== "1") {
      notification({
        type: "error",
        message: "Only one entry can be 'Send Immediately' per batch."
      })();
      return;
    }
    onUpdate(index, {
      ...entry,
      notification_send_type: value,
      n_date_time: value === "1" ? "" : entry.n_date_time
    });
  };

  const handleDateTimeChange = (newDate) => {
    if (newDate)
      onUpdate(index, {
        ...entry,
        n_date_time: moment(newDate).format("DD-MM-YYYY hh:mm A")
      });
  };

  const handleShowOnHomeChange = (e) =>
    onUpdate(index, { ...entry, show_on_home: e.target.checked ? 1 : 0 });

  const isScheduled =
    entry.notification_send_type === "0" || entry.notification_send_type === 0;

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: isMain && !isSent ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderLeft: isMain && !isSent ? "4px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: isSent ? "#f5f5f5" : isMain ? "rgba(25, 118, 210, 0.04)" : "#fff",
        position: "relative"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isSent ? (
            <Typography
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 600
              }}
            >
              Sent — Locked
            </Typography>
          ) : (
            isMain && (
              <Typography
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: 600
                }}
              >
                Main
              </Typography>
            )
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Schedule Entry {index + 1}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Show on Home
            </Typography>
            <Switch
              size="small"
              checked={entry.show_on_home === 1}
              onChange={handleShowOnHomeChange}
              disabled={effectiveReadOnly}
            />
          </Box>
          <Tooltip
            title={
              isSent
                ? "Sent entries cannot be removed"
                : totalEntries <= 1
                  ? "Cannot remove the only entry"
                  : "Remove this entry"
            }
          >
            <span>
              <IconButton
                color="error"
                onClick={() => onRemove(index)}
                disabled={totalEntries <= 1 || effectiveReadOnly}
                size="small"
              >
                <DeleteSharp fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              inputFormat="dd/MM/yyyy"
              value={entry.s_date ? moment(entry.s_date, "DD-MM-YYYY").toDate() : null}
              minDate={isEditMode ? undefined : new Date()}
              onChange={handleStartDateChange}
              disabled={effectiveReadOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onKeyDown={(e) => e.preventDefault()}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              inputFormat="dd/MM/yyyy"
              value={entry.e_date ? moment(entry.e_date, "DD-MM-YYYY").toDate() : null}
              minDate={
                entry.s_date
                  ? moment(entry.s_date, "DD-MM-YYYY").toDate()
                  : isEditMode
                    ? undefined
                    : new Date()
              }
              onChange={handleEndDateChange}
              disabled={effectiveReadOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onKeyDown={(e) => e.preventDefault()}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <RadioGroup row value={entry.notification_send_type} onChange={handleSendTypeChange}>
            <FormControlLabel
              value="0"
              control={<Radio size="small" disabled={effectiveReadOnly} />}
              label="Schedule for later"
            />
            <FormControlLabel
              value="1"
              control={<Radio size="small" disabled={effectiveReadOnly} />}
              label="Send Immediately"
            />
          </RadioGroup>
        </Grid>
        {isScheduled && (
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Notification Send Date & Time"
                inputFormat="dd/MM/yyyy hh:mm a"
                value={
                  entry.n_date_time
                    ? moment(entry.n_date_time, "DD-MM-YYYY hh:mm A").toDate()
                    : null
                }
                minDateTime={isEditMode ? undefined : moment().startOf("minute").toDate()}
                onChange={handleDateTimeChange}
                disabled={effectiveReadOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    onKeyDown={(e) => e.preventDefault()}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
export default ScheduleEntryRow;
